import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from 'seagull';
import { RichTextEditor } from 'seagull/components/RichTextEditor/RichTextEditor';
import { RichTextViewer } from 'seagull/components/RichTextViewer/RichTextViewer';
import { getTestIds } from 'seagull/utils/TestIds';
import { useOverlayContext, useStore } from '~/contexts';
import { useSaveCustomPageText } from '~/hooks/reactQuery/mutations/useSaveCustomPageText';
import { useCustomPageText } from '~/hooks/reactQuery/queries/useCustomPageText';
import { useLocaleKeys } from '~/i18n';
import { selectors } from '~/store/state-selectors';
export const GetTextPageContentTID = (parentTestId)=>getTestIds(parentTestId);
const EMPTY_QUILL_DELTA = {
    type: 'quill-delta',
    value: {
        ops: []
    }
};
const DEBOUNCE_MS = 1000;
const isQuillContentEmpty = (value)=>{
    var _value_value;
    const ops = value === null || value === void 0 ? void 0 : (_value_value = value.value) === null || _value_value === void 0 ? void 0 : _value_value.ops;
    if (ops == null || ops.length === 0) return true;
    return ops.every((op)=>typeof op.insert === 'string' && op.insert.trim() === '');
};
export const TextPageContent = (param)=>{
    let { textId, onTextIdChange, disabled, submitAttempted, testId } = param;
    const { t } = useLocaleKeys();
    const testIds = useMemo(()=>GetTextPageContentTID(testId), [
        testId
    ]);
    const businessId = useStore(selectors.user.businessId);
    const { content: loadedContent, isLoading } = useCustomPageText({
        textId,
        enabled: textId !== ''
    });
    const parsedContent = useMemo(()=>{
        if (loadedContent == null || loadedContent === '') return EMPTY_QUILL_DELTA;
        try {
            return JSON.parse(loadedContent);
        } catch (unused) {
            return EMPTY_QUILL_DELTA;
        }
    }, [
        loadedContent
    ]);
    const editorContentRef = useRef(parsedContent);
    const debounceRef = useRef();
    const textIdRef = useRef(textId);
    useEffect(()=>{
        textIdRef.current = textId;
    }, [
        textId
    ]);
    useEffect(()=>{
        editorContentRef.current = parsedContent;
    }, [
        parsedContent
    ]);
    const [currentEmpty, setCurrentEmpty] = useState(()=>isQuillContentEmpty(parsedContent));
    useEffect(()=>{
        setCurrentEmpty(isQuillContentEmpty(parsedContent));
    }, [
        parsedContent
    ]);
    const { saveText } = useSaveCustomPageText();
    const { showErrorToast } = useOverlayContext();
    const [hasEdited, setHasEdited] = useState(false);
    const handleChange = useCallback((newValue)=>{
        editorContentRef.current = newValue;
        setCurrentEmpty(isQuillContentEmpty(newValue));
        if (!hasEdited) setHasEdited(true);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(async ()=>{
            if (disabled) return;
            try {
                const result = await saveText({
                    businessId: businessId !== null && businessId !== void 0 ? businessId : '',
                    content: JSON.stringify(newValue)
                });
                if (textIdRef.current === '' && result.textId) {
                    onTextIdChange === null || onTextIdChange === void 0 ? void 0 : onTextIdChange(result.textId);
                }
            } catch (unused) {
                showErrorToast(t.customPages.templates.saveTextError());
            }
        }, DEBOUNCE_MS);
    }, [
        businessId,
        disabled,
        hasEdited,
        onTextIdChange,
        saveText,
        showErrorToast,
        t
    ]);
    useEffect(()=>{
        return ()=>{
            clearTimeout(debounceRef.current);
        };
    }, []);
    const showError = !disabled && currentEmpty && !!submitAttempted && (textId !== '' || hasEdited);
    if (isLoading) {
        return /*#__PURE__*/ _jsx(Box, {
            testId: testIds.TextPageContentTestIds.Root(),
            children: /*#__PURE__*/ _jsx(Typography, {
                testId: testIds.TextPageContentTestIds.Loading(),
                variant: "paragraph2",
                children: t.components.loadingIndicator.loading()
            })
        });
    }
    return /*#__PURE__*/ _jsxs(Box, {
        testId: testIds.TextPageContentTestIds.Root(),
        children: [
            disabled ? /*#__PURE__*/ _jsx(RichTextViewer, {
                value: parsedContent,
                testId: testIds.TextPageContentTestIds.Viewer()
            }) : /*#__PURE__*/ _jsx(RichTextEditor, {
                testId: testIds.TextPageContentTestIds.Editor(),
                initialValue: parsedContent,
                onChange: handleChange
            }),
            showError && /*#__PURE__*/ _jsx(Typography, {
                testId: testIds.TextPageContentTestIds.ErrorText(),
                variant: "paragraph2",
                color: "error",
                children: t.customPages.textContent.emptyContentError()
            })
        ]
    });
};
